/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createReducer } from '@reduxjs/toolkit';
import { Coordinates, Position } from 'app';

import { startDebug, startFree, startTournament, startTraining } from '~/game';

import {
  HorseField,
  moveCamera,
  setCameraMode,
  setFollowZoomStops,
  setHorse,
  setHorseField,
  setHorses,
  setHorsesField,
  setHurdles,
  setMoves,
} from './actions';
import { loadFollowZoomStops } from './cameraStorage';
import {
  bootstrapScaleFromFollowStops,
  clampFollowZoomStops,
  DEFAULT_FOLLOW_ZOOM_STOPS,
} from './cameraZoom';
import { BoardHurdle } from './hurdleTypes';

/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

export interface Horse {
  // index of horse in controller horse array
  index: number;

  // index of player in controller player array
  playerId: number;

  // in-game color of horse, can differ between clients
  color: number;

  // jockey name of horse set by server in remote game
  jockeyName: string;

  // real color if available for real horses before assigning in-game color
  // if not set or computer horse this is always -1
  realColor: number;

  // horse name set by server in remote game
  name: string;

  // date string of the next date when this horse gets replaced by a bot or skipped depending on game mode
  // will be used to create progress circle animation
  nextTimeoutDateString?: string;
}

export type CameraMode = 'fixed' | 'follow';

export interface CameraState extends Position {
  mode: CameraMode;
  /** Exposure-style stops for follow zoom (see cameraZoom). Persisted. */
  followZoomStops: number;
}

export interface BoardState {
  hurdles: BoardHurdle[];
  moves: Coordinates[][];
  horses: Horse[];
  horseFields: HorseField[];
  camera: CameraState;
}

/** Build initial board state; hydrates follow zoom stops from localStorage when in browser. */
export function getInitialBoardState(): BoardState {
  const stored = loadFollowZoomStops();
  const followZoomStops =
    stored !== null ? clampFollowZoomStops(stored) : DEFAULT_FOLLOW_ZOOM_STOPS;
  const bootstrapScale = bootstrapScaleFromFollowStops(followZoomStops);
  return {
    hurdles: [],
    moves: [],
    horses: [],
    horseFields: [],
    camera: {
      x: 0.5,
      y: 0.5,
      /* Bootstrap scale from stops alone; saga replaces with computed follow target when running. */
      scale: bootstrapScale,
      rotation: 0,
      mode: 'follow',
      followZoomStops,
    },
  };
}

export const initialBoardState: BoardState = getInitialBoardState();

export default createReducer(initialBoardState, (builder) => {
  builder
    .addCase(startTraining, () => getInitialBoardState())
    .addCase(startFree, () => getInitialBoardState())
    .addCase(startDebug, () => getInitialBoardState())
    .addCase(startTournament, () => getInitialBoardState())
    .addCase(setHurdles, (state, action) => {
      state.hurdles = action.payload;
    })
    .addCase(setMoves, (state, action) => {
      state.moves = action.payload;
    })
    .addCase(setHorses, (state, action) => {
      state.horses = action.payload;
      const n = action.payload.length;
      if (state.horseFields.length !== n) {
        // Keep per-index slots aligned with horses[] so selectors never read stale indices
        // after horse count changes (e.g. JOINGAMEOPP) before the next setHorsesField lands.
        state.horseFields.length = n;
      }
    })
    .addCase(setHorse, (state, action) => {
      const horse = action.payload;
      state.horses = state.horses.map((h) => (h.index === horse.index ? horse : h));
    })
    .addCase(setHorsesField, (state, action) => {
      const fields = action.payload;
      state.horseFields = fields.map((fieldPayload) => {
        const { x, y, animationIntent, pos, prog } = fieldPayload;
        if (animationIntent?.path?.length) {
          const path = animationIntent.path;
          const last = path[path.length - 1];
          return {
            x: last.x,
            y: last.y,
            pos: pos ?? 0,
            prog: prog ?? 0,
            animationIntent,
          } as HorseField;
        }
        return { x, y, pos: pos ?? 0, prog: prog ?? 0 } as HorseField;
      });
    })
    .addCase(setHorseField, (state, action) => {
      const index = action.payload.index;
      const field = action.payload.value;
      if (field.animationIntent?.path?.length) {
        const path = field.animationIntent.path;
        const last = path[path.length - 1];
        state.horseFields[index] = {
          x: last.x,
          y: last.y,
          pos: field.pos ?? 0,
          prog: field.prog ?? 0,
          animationIntent: field.animationIntent,
        } as HorseField;
        return;
      }
      state.horseFields[index] = {
        x: field.x,
        y: field.y,
        pos: field.pos ?? 0,
        prog: field.prog ?? 0,
      } as HorseField;
    })
    .addCase(moveCamera, (state, action) => {
      state.camera.x = action.payload.x;
      state.camera.y = action.payload.y;
      state.camera.scale = action.payload.scale;
    })
    .addCase(setCameraMode, (state, action) => {
      state.camera.mode = action.payload;
    })
    .addCase(setFollowZoomStops, (state, action) => {
      state.camera.followZoomStops = clampFollowZoomStops(action.payload);
    });
});
